import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import {
    FilterBar,
    FilterItemConfig,
} from '@/components/ui/data-table-customized'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getAffiliatePendingInvoices } from '@/modules/Affiliate/services'
import { getPendingInvoiceStatusFilterOptions } from '@/modules/Affiliate/constants'
import { PendingInvoice } from '@/modules/Affiliate/types'
import { formatFromUTC } from '@/utils/function'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface PendingInvoicesProps {
    affiliateId: number
}

interface PendingInvoiceFilters {
    status: string
}

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND',
        maximumFractionDigits: 0,
    }).format(value)
}

const PendingInvoices: React.FC<PendingInvoicesProps> = ({ affiliateId }) => {
    const { t } = useTranslation('affiliate')

    const {
        params,
        filters,
        queryParams,
        setParam,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<PendingInvoiceFilters>({
        defaults: { page: 1, page_size: 10 },
        filterDefaults: { status: '' },
    })

    const { control } = useForm<PendingInvoiceFilters>({
        defaultValues: filters,
        values: filters,
    })

    const { data, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.AFFILIATE_PENDING_INVOICES,
            'affiliate-pending-invoices',
            affiliateId,
            queryParams,
            filters,
        ] as const,
        queryFn: () =>
            getAffiliatePendingInvoices(affiliateId, {
                page: params.page,
                page_size: params.page_size,
                q: queryParams.q,
                status: filters.status,
            }),
        enabled: !!affiliateId,
        placeholderData: true,
    })

    const invoices = data?.results || []
    const pagination = data?.pagination
    const stateComponent = renderState()

    const filterItems: FilterItemConfig[] = useMemo(
        () => [
            {
                name: 'status',
                type: 'select',
                label: t('status'),
                placeholder: t('status'),
                options: getPendingInvoiceStatusFilterOptions(t),
            },
        ],
        [t]
    )

    const columns: DataTableColumn<PendingInvoice>[] = useMemo(
        () => [
            {
                key: 'invoice_id',
                title: t('invoice_id'),
                width: 100,
                formatter: {
                    type: 'link',
                    pathKey: 'invoiceDetail',
                    pathParams: (value) => ({ id: String(value) }),
                },
            },
            {
                key: 'customer_name',
                title: t('customer_name'),
                width: 200,
                formatter: {
                    type: 'link',
                    pathKey: 'customerDetail',
                    pathParams: (_value, row) => ({
                        id: String(row.customer_id),
                    }),
                },
            },
            {
                key: 'invoice_amount',
                title: t('amount'),
                width: 150,
                render: (value) => formatCurrency(value),
            },
            {
                key: 'invoice_status',
                title: t('status'),
                width: 130,
                render: (value) =>
                    t(`invoice_status_${value}`) || value?.replace(/-/g, ' '),
            },
            {
                key: 'commission_percent',
                title: t('rate'),
                width: 80,
                render: (value) => `${value}%`,
            },
            {
                key: 'expected_net_commission',
                title: t('expected_commission'),
                width: 160,
                render: (value) => formatCurrency(value),
            },
            {
                key: 'invoice_created_at',
                title: t('created_at'),
                width: 140,
                render: (value) => formatFromUTC(value, 'DD/MM/YYYY HH:mm'),
            },
        ],
        [t]
    )

    const handlePageChange = (page: number) => setParam('page', page, false)
    const handlePageSizeChange = (size: number) => {
        setParam('page_size', size)
        setParam('page', 1)
    }

    return (
        <Paper className="space-y-2 my-2">
            <FilterBar
                control={control}
                setFilter={setFilter}
                items={filterItems}
                hideSearch
            />
            {stateComponent || (
                <DataTableCustomized<PendingInvoice>
                    columns={columns}
                    rows={invoices}
                    rowKey={(row) => row.invoice_id}
                    size="small"
                    toolbar={{
                        search: {
                            ...searchInputProps,
                            placeholder: t('search_invoice'),
                        },
                        settings: {
                            show: true,
                            storageKey: 'affiliate-pending-invoices',
                        },
                    }}
                    stickyHeader={{
                        enabled: true,
                        offsetTopSelector: '#main-header',
                    }}
                    pagination={{
                        page: params.page,
                        pageSize: params.page_size,
                        total: pagination?.total || 0,
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange,
                    }}
                />
            )}
        </Paper>
    )
}

export default PendingInvoices
